import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Stack, Button, CircularProgress,
} from '@mui/material';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import { supabase } from '../../supabaseClient.js';
import useAlert from '../../alert/useAlert.js';
import ProfessionalVerificationForm, { deriveKey, encryptFile } from '../../components/ProfessionalVerificationForm.js';
import ProfileAutofill from '../../components/ProfileAutofill';
import { buildPrefillOptionLists, registryPrefillFields, REGISTRY_KEYS, isBlankValue } from '../../utils/profilePrefill';
import OnboardingLayout from './OnboardingLayout.js';

const TEAL = '#29C0BB';

const Step4Verification = ({ ctx, onContinue, onSkip, onStepClick }) => {
  const { userId, email, initials, displayName } = ctx;
  const { setAlert } = useAlert();
  const [practiceTitle, setPracticeTitle] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [qualification, setQualification] = useState('');
  const [university, setUniversity] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [supportingDocUrl, setSupportingDocUrl] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const verificationRef = useRef(null);
  const [registryAutofillState, setRegistryAutofillState] = useState({ loading: true });

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('user_accounts')
        .select('practice_title, organisation, professional_qualification, experience, university, supporting_document_url')
        .eq('id', userId)
        .maybeSingle();
      if (!data) return;
      const [titleRes, orgRes] = await Promise.all([
        supabase.rpc('decrypt_text', { encrypted_data: data.practice_title }),
        supabase.rpc('decrypt_text', { encrypted_data: data.organisation }),
      ]);
      setPracticeTitle(titleRes.data || '');
      setOrganisation(orgRes.data || '');
      setQualification(data.professional_qualification || '');
      setExperienceYears(data.experience ?? '');
      setUniversity(data.university || '');
      setSupportingDocUrl(data.supporting_document_url || '');
      setProfileLoaded(true);
    };
    load();
  }, [userId]);

  const autofillFields = [
    ...(profileLoaded ? [
      { key: 'practice_title', label: 'Practice Title', current: practiceTitle, set: setPracticeTitle },
      { key: 'organisation', label: 'Organization', current: organisation, set: setOrganisation },
      { key: 'university', label: 'University', current: university, set: setUniversity },
      { key: 'professional_qualification', label: 'Professional Qualification', current: qualification, set: setQualification },
      { key: 'experience', label: 'Experience (years)', current: experienceYears, set: setExperienceYears },
    ] : []),
    ...registryPrefillFields(registryAutofillState),
  ];
  const applyAutofill = (rows) => {
    const applied = [];
    rows.forEach(row => {
      const field = autofillFields.find(item => item.key === row.key);
      if (field?.set && isBlankValue(field.current)) { field.set(row.value); applied.push(row); }
    });
    return [...applied, ...(verificationRef.current?.applyAutofill(rows.filter(row => REGISTRY_KEYS.includes(row.key))) || [])];
  };
  const undoAutofill = (rows) => {
    verificationRef.current?.undoAutofill(rows);
    rows.forEach(row => {
      const field = autofillFields.find(item => item.key === row.key);
      if (field?.set && String(field.current) === String(row.value)) field.set('');
    });
  };

  const handleSupportingDocUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setAlert('Document size should be less than 10MB', 'error');
      return;
    }
    setUploadingDoc(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${userId}/supporting/${fileName}`;
      const key = await deriveKey(userId);
      const encryptedBlob = await encryptFile(file, key, fileName);
      const { error: uploadError } = await supabase.storage
        .from('tikcare-attachments')
        .upload(filePath, encryptedBlob, { cacheControl: '3600', upsert: true });
      if (uploadError) throw uploadError;
      const { data: publicUrlData } = supabase.storage
        .from('tikcare-attachments')
        .getPublicUrl(filePath);
      const publicUrl = publicUrlData.publicUrl;
      const { error: dbError } = await supabase
        .from('user_accounts')
        .update({ supporting_document_url: publicUrl })
        .eq('id', userId);
      if (dbError) throw dbError;
      setSupportingDocUrl(publicUrl);
      setAlert('Supporting document uploaded.', 'success');
    } catch (error) {
      console.error('Error uploading supporting document:', error);
      setAlert('Failed to upload document. Please try again.', 'error');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleContinue = async () => {
    setSaving(true);
    const { error } = await supabase
      .from('user_accounts')
      .update({
        practice_title: practiceTitle,
        organisation: organisation,
        professional_qualification: qualification,
        university: university,
        experience: experienceYears,
      })
      .eq('id', userId);
    setSaving(false);
    if (error) {
      setAlert('Unable to save your details. Please try again.', 'error');
      return;
    }
    onContinue();
  };

  return (
    <OnboardingLayout
      step={4}
      title="Professional verification"
      description="Verify your professional credentials to become eligible for client referrals."
      sidebarExtra={
        <Stack spacing={2}>
          <Box sx={{ p: 2, backgroundColor: '#f0fbfa', borderRadius: 2 }}>
            <Stack direction="row" spacing={1}>
              <VerifiedUserOutlinedIcon sx={{ color: TEAL, fontSize: 20, mt: '2px' }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1D8A87' }}>
                  Already verified in our system
                </Typography>
                <Typography variant="body2" sx={{ color: '#5b6770', mt: 0.5 }}>
                  If your Hong Kong psychologist registration is found in our official registry,
                  we'll verify it automatically. If we can't confirm it, we'll email you with next steps.
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <HeadsetMicOutlinedIcon sx={{ color: '#5b6770', fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: '#5b6770' }}>
              <strong>Need help?</strong> Contact us at support@tikcare.com
            </Typography>
          </Stack>
        </Stack>
      }
      avatarInitials={initials}
      onStepClick={onStepClick}
      onSkip={onSkip}
      skipCaption="You can complete verification later from your dashboard."
      onContinue={handleContinue}
      continueLabel="Verify and continue"
      saving={saving}
    >
      <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f6f8f9', borderRadius: 2, display: 'flex', gap: 1 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 18, color: TEAL, mt: '2px' }} />
        <Typography variant="body2" sx={{ color: '#5b6770' }}>
          All information is private and securely stored. We use official registries to verify your
          credentials, and manual review will be provided if needed.
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <ProfileAutofill fields={autofillFields} optionLists={buildPrefillOptionLists()} onApply={applyAutofill} onUndo={undoAutofill}
          saveLabel="Verify and continue" appliedNote="Registry details are drafts: use Verify in the registration section to check and save them." />
      </Box>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        {/* Left: Professional Registration */}
        <Box sx={{ flex: 1, border: '1px solid #e6eaee', borderRadius: 3, p: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 2 }}>
            Professional Registration <Typography component="span" variant="body2" sx={{ color: '#8a94a0' }}>(Hong Kong)</Typography>
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Practice title *</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. Clinical Psychologist"
            value={practiceTitle}
            onChange={(e) => setPracticeTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Organization / Affiliation *</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. Hong Kong Psychological Society"
            value={organisation}
            onChange={(e) => setOrganisation(e.target.value)}
            sx={{ mb: 2 }}
          />
          <ProfessionalVerificationForm ref={verificationRef} onAutofillStateChange={setRegistryAutofillState} userid={userId} username={displayName} useremail={email} />
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f0fbfa', borderRadius: 2, display: 'flex', gap: 1 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: TEAL, mt: '2px' }} />
            <Typography variant="body2" sx={{ color: '#5b6770' }}>
              We'll check your registration against the official Hong Kong Psychologist Register
              for automatic verification.
            </Typography>
          </Box>
        </Box>

        {/* Right: Qualification / Degree */}
        <Box sx={{ flex: 1, border: '1px solid #e6eaee', borderRadius: 3, p: 3, alignSelf: 'flex-start' }}>
          <Typography sx={{ fontWeight: 700, mb: 2 }}>Professional Qualification / Degree</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Degree / Qualification *</Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Professional membership or qualification — one entry per line"
            value={qualification}
            onChange={(e) => setQualification(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>University *</Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Degree and university — one entry per line"
            value={university}
            onChange={(e) => setUniversity(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Years of Experience *</Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 0, max: 80, step: 1 }}
            placeholder="Enter your years of experience"
            value={experienceYears}
            onChange={(e) => setExperienceYears(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Supporting Document <Typography component="span" variant="caption" sx={{ color: '#8a94a0' }}>(Optional)</Typography>
          </Typography>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{
              textTransform: 'none',
              borderStyle: 'dashed',
              borderColor: TEAL,
              color: TEAL,
              py: 2,
              '&:hover': { borderColor: '#1da8a3', backgroundColor: '#e0f2f1' },
            }}
          >
            {uploadingDoc ? <CircularProgress size={20} sx={{ color: TEAL }} /> : 'Upload document'}
            <input
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleSupportingDocUpload}
              disabled={uploadingDoc}
            />
          </Button>
          <Typography variant="caption" sx={{ color: '#8a94a0', display: 'block', mt: 0.5, textAlign: 'center' }}>
            PDF, JPG or PNG (Max 10MB)
          </Typography>
          {supportingDocUrl && (
            <Typography variant="caption" sx={{ color: '#1da8a3', display: 'block', mt: 1 }}>
              Supporting document uploaded.
            </Typography>
          )}
        </Box>
      </Stack>
    </OnboardingLayout>
  );
};

export default Step4Verification;
